import { useEffect } from "react";
import { Form, Checkbox, Typography } from "antd";
import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import FormFooter from "../../components/CustomForms/FormFooter";
import { useSelector, useDispatch } from "react-redux";
import { BTN_TXT, FILE_TYPES } from "../../constants/constants";
import EntityTagSelector from "./EntityTagSelector";
import OptionsBuilder from "./OptionsBuilder";
import { addQuestion, updateQuestion, getQuestions, getQuestionDropdowns } from "../../redux/slices/QuestionBankSlice";

const { Text } = Typography;

const AddQuestionForm = ({ onClose, editData }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    dropdownData,
    addQuestionLoading,
    updateQuestionLoading,
    addQuestionError,
    updateQuestionError,
  } = useSelector((state) => state.questionBank);
  const questionType = Form.useWatch("questionTypeId", form);
  const selectedCategory = Form.useWatch("categoryId", form);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        categoryId: editData.categoryId,
        questionTypeId: editData.questionTypeId,
        questionTitle: editData.questionTitle,
        description: editData.description,
        placeholder: editData.placeholder,
        options: editData.answerOptions || [],
        fileType: editData.answerOptions,
        isRequired: editData.isRequired,
        entityTags: editData.entityTypes?.map((e) => e.entityTypeId) || [],
      });
    } else {
      form.resetFields();
    }
  }, [editData, form]);

  const SHOW_PLACEHOLDER_TYPES = [
    "QTM1000001", // Input Text
    "QTM1000002", // Text Area
    "QTM1000003", // Number
    "QTM1000005", // Dropdown
    "QTM1000006", // Radio
    "QTM1000007", // Checkbox
  ];

  const OPTION_TYPES = [
    "QTM1000005", // Dropdown
    "QTM1000006", // Radio
    "QTM1000007", // Checkbox
  ];

  const FILE_UPLOAD_TYPE = "QTM1000008";

  const handleFinish = async (values) => {
    const payload = {
      ...values,
      isRequired: values.isRequired || false,
      entityTags: values.entityTags?.filter((tag) => tag !== "ALL") || [],
    };

    let res;

    if (editData) {
      res = await dispatch(
        updateQuestion({
          questionId: editData.questionId,
          ...payload,
        }),
      );
    } else {
      res = await dispatch(addQuestion(payload));
    }

    if (res?.payload?.statusCode === (editData ? 200 : 201)) {
      onClose();
      form.resetFields();
      dispatch(getQuestions());
      dispatch(getQuestionDropdowns());
    }
  };

  return (
    <Form
      layout="vertical"
      className="add-question-form"
      form={form}
      onFinish={handleFinish}
    >
      {/* Category */}
      <CustomSelect
        label="Question Category"
        name="categoryId"
        isRequired
        placeholder="Select a category"
        rules={[{ required: true, message: "Please select category" }]}
        options={[
          ...(dropdownData?.questionCatogory?.map((category) => ({
            value: category.categoryId,
            label: category.categoryName,
          })) || []),
          { value: "OTHERS", label: "Others" },
        ]}
      />

      {selectedCategory === "OTHERS" && (
        <CustomInput
          label="Custom Category Name"
          name="customCategoryName"
          isRequired
          placeholder="Enter custom category name"
          rules={[{ required: true, message: "Please enter custom category name" }]}
        />
      )}

      {/* Type */}
      <CustomSelect
        label="Question Type"
        name="questionTypeId"
        isRequired
        placeholder="Select question type"
        rules={[{ required: true, message: "Please select question type" }]}
        options={dropdownData?.questionType?.map((type) => ({
          value: type.questionTypeId,
          label: type.questionTypeName,
        }))}
      />

      {/* Title */}
      <CustomInput
        label="Question Title"
        name="questionTitle"
        isRequired
        placeholder="Enter question text"
        rules={[{ required: true, message: "Please enter question title" }]}
      />

      {/* Description */}
      <CustomInput
        label="Description"
        name="description"
        placeholder="Add helper text or contextual notes"
        isTextArea
      />

      {SHOW_PLACEHOLDER_TYPES.includes(questionType) && (
        <CustomInput
          label="Placeholder Text (Optional)"
          name="placeholder"
          placeholder="Enter placeholder text"
        />
      )}

      {OPTION_TYPES.includes(questionType) && (
        <Form.Item
          label={
            <span>
              Options<Text type="danger"> *</Text>
            </span>
          }
          name="options"
          className="entity-tag-form-item"
          rules={[{ required: true, message: "Add at least one option" }]}
        >
          <OptionsBuilder />
        </Form.Item>
      )}

      {questionType === FILE_UPLOAD_TYPE && (
        <CustomSelect
          label="File Type"
          name="fileType"
          isRequired
          mode="multiple"
          placeholder="Select File type"
          rules={[{ required: true, message: "Please select file type" }]}
          options={FILE_TYPES?.map((type) => ({
            value: type,
            label: type,
          }))}
        />
      )}

      {/* Required */}
      <Form.Item name="isRequired" valuePropName="checked">
        <Checkbox>Mark as Required Field</Checkbox>
      </Form.Item>

      {/* Company Type Tags */}
      <Form.Item
        label={
          <span>
            Company Type Tags<Text type="danger"> *</Text>
          </span>
        }
        name="entityTags"
        className="entity-tag-form-item"
        rules={[
          { required: true, message: "Select at least one company type" },
        ]}
      >
        <EntityTagSelector
          options={[
            ...dropdownData?.entityType?.map((e) => ({
              value: e.entityTypeId,
              label: e.entityTypeName,
            })),
            { value: "ALL", label: "All" },
          ]}
        />
      </Form.Item>

      <FormFooter
        onCancelClick={() => {
          onClose();
          form.resetFields();
        }}
        submitLoading={addQuestionLoading || updateQuestionLoading}
        error={addQuestionError || updateQuestionError}
        btnName={BTN_TXT.SUBMIT}
      />
    </Form>
  );
};

export default AddQuestionForm;
